import React, { LegacyRef } from 'react';
import styled from 'styled-components';
import {
  screenXsMin,
  screenSmMin,
  screenMdMin,
} from '@spotify-internal/encore-web';

import { AnchorLogoWithWordmark } from 'client/components/svgs/AnchorLogoWithWordmark';
import { RetinaPicture } from 'client/components/RetinaPicture';
import { PODCAST_COLORS } from 'client/utils';
import { useT } from 'client/i18n/hooks/useT';
import { INTEGRATION_CONNECTION_MENU_MODE } from '../constants';
import { LoginSignUp } from '.';

const HeroSection = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: calc(100vw * 0.5625);
  overflow: hidden;

  picture {
    position: absolute;
    top: 0;
    left: 50%;
    min-height: 100%;
    transform: translateX(-50%);
  }

  @media (max-width: 1400px) {
    height: 800px;
  }

  @media (max-width: ${screenXsMin}) {
    height: 450px;
  }
`;

const BackgroundRetinaPicture = styled(RetinaPicture)`
  position: absolute;
  top: 0;
  left: 50%;
  min-height: 100%;
  max-height: 100%;
  transform: translateX(-50%);

  @media (max-width: ${screenMdMin}) {
    left: 45%;
    transform: translateX(-45%);
  }
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 34%;
  height: 100%;
  padding: 20px 0 20px 4%;
  background: linear-gradient(
    to left,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 175%
  );

  @media (max-width: ${screenMdMin}) {
    max-width: 50%;
  }
`;

const HeroTitle = styled.h1`
  margin-bottom: 25px;
  color: ${PODCAST_COLORS.WHITE.color};
  font-size: 6.25rem;
  line-height: 90px;
  letter-spacing: -0.02em;

  span {
    display: block;
    margin-top: 14px;
    font-size: 3.125rem;
    line-height: 50px;
    letter-spacing: -0.01em;

    @media (max-width: ${screenSmMin}) {
      margin-top: 0;
      font-size: 2.25rem;
    }

    @media (max-width: ${screenXsMin}) {
      font-size: 1.5rem;
    }
  }

  @media (max-width: ${screenSmMin}) {
    margin-bottom: 14px;
    font-size: 4.5rem;
    line-height: 70px;
  }

  @media (max-width: ${screenXsMin}) {
    font-size: 3rem;
    line-height: 50px;
  }
`;

const HeroBody = styled.p`
  margin: 0;
  color: ${PODCAST_COLORS.WHITE.color};
  font-size: 1.563rem;
  line-height: 28px;
  letter-spacing: -0.01em;

  @media (max-width: ${screenSmMin}) {
    font-size: 1.375rem;
    line-height: 26px;
    letter-spacing: -0.008em;
  }

  @media (max-width: ${screenXsMin}) {
    font-size: 1rem;
    line-height: 19px;
  }
`;

const AnchorSVGWrapper = styled.div`
  position: absolute;
  top: 20px;
  min-width: 166px;
  svg {
    width: 100%;
  }

  @media (max-width: ${screenXsMin}) {
    min-width: 120px;
  }
`;

export function Hero({
  currentAuthFormView,
  loginSignUpRef,
  returnToRedirectPath,
  setCurrentAuthFormView,
  showHeroLoginSignUpForm,
}: {
  currentAuthFormView: INTEGRATION_CONNECTION_MENU_MODE;
  loginSignUpRef: LegacyRef<HTMLDivElement> | undefined;
  returnToRedirectPath: string;
  setCurrentAuthFormView: (value: INTEGRATION_CONNECTION_MENU_MODE) => void;
  showHeroLoginSignUpForm: boolean;
}) {
  const t = useT();

  return (
    <HeroSection>
      <BackgroundRetinaPicture
        fallbackExtension="jpg"
        imagePath="/videopartner/videopartner-hero-background-image"
        alt={t(
          'IntegrationConnectionScreen.Hero.ImageAltText',
          'Woman recording a video podcast with a ring light',
          'Alt text for the background image on the Integration Connection Screen'
        )}
      />
      <HeroContent>
        <AnchorSVGWrapper>
          <AnchorLogoWithWordmark
            ariaLabel={t(
              'svgs.AnchorLogoWithWordmark.BySpotify',
              'by Spotify',
              'Anchor logo with "by Spotify" added after it'
            )}
            includeSpotify={true}
            fillColor={PODCAST_COLORS.WHITE.color}
          />
        </AnchorSVGWrapper>
        <HeroTitle
          dangerouslySetInnerHTML={{
            __html: t(
              'IntegrationConnectionScreen.Hero.Title',
              'Video Podcasts <span>by Spotify</span>',
              'The title for the Integration Connection Screen for Video Podcasts'
            ),
          }}
        />
        <HeroBody>
          {t(
            'IntegrationConnectionScreen.Hero.Body',
            'Create a podcast that fans can watch (or listen to) on Spotify.',
            'The body for the Integration Connection Screen for Video Podcasts'
          )}
        </HeroBody>
      </HeroContent>
      {showHeroLoginSignUpForm && (
        <LoginSignUp
          reference={loginSignUpRef}
          currentAuthFormView={currentAuthFormView}
          returnToRedirectPath={returnToRedirectPath}
          setCurrentAuthFormView={setCurrentAuthFormView}
          showHeroLoginSignUpForm={showHeroLoginSignUpForm}
        />
      )}
    </HeroSection>
  );
}
